import {
  useLayoutEffect,
  useState,
  useEffect,
  useCallback,
  useRef,
} from "react";
import DataGrid, {
  Column,
  Export,
  HeaderFilter,
  Pager,
  Paging,
  SearchPanel,
} from "devextreme-react/data-grid";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faRefresh } from "@fortawesome/free-solid-svg-icons";
import moment from "moment";
import { Template } from "devextreme-react";
import {
  DefaultDateFormat,
  IconWithTooltip,
  AssetQuotationType,
  AssetSubStatus,
} from "../../../helper/fixcode";
import { Loader } from "../../Shared/PageLoader";
import { assetService } from "../../../apis/apiServices/Asset";
import { reportService } from "../../../apis/apiServices/Report";

export default function ReportAssetPurchaseComponent(props) {
  const navigate = useNavigate();
  const user = useSelector((state) => state.user.data);
  const [assetPurchaseList, setAssetPurchaseList] = useState([]);
  const refDataGrid = useRef(null);
  const [state, setState] = useState({
    isCompleteLoading: false,
    loadingMessage: "Loading Report...",
  });

  useLayoutEffect(() => {
    document.querySelector("#pageTitle").innerHTML = `Asset Purchase`;
  }, []);

  let isUseEffectRendered = false;
  useEffect(() => {
    const controller = new AbortController();
    try {
      if (isUseEffectRendered) return;
      fetchApiOnPageLoad();
      return () => {
        controller.abort();
        isUseEffectRendered = true;
      };
    } catch (error) {
      console.log(error);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchApiOnPageLoad = useCallback(async () => {
    try {
      setState((prev) => ({ ...prev, isCompleteLoading: true }));
      await getAssetPurchaseList();
    } catch (error) {
      console.log(error);
    } finally {
      setState((prev) => ({ ...prev, isCompleteLoading: false }));
    }
  }, []);

  const getAssetPurchaseList = async () => {
    try {
      let body = {
        requestQuotationId: 0,
        statusId: 0,
        userId: user?.userId,
        quotationTypeId: AssetQuotationType.RequestForAssetPurchase,
      };
      const response = await reportService.getAssetQuotationList(body);
      setAssetPurchaseList(response?.data ?? []);
    } catch (error) {
      console.log(error);
    }
  };

  const renderDate = (raw) => {
    if (!raw) return "";
    // try ISO first
    const iso = moment(raw, moment.ISO_8601, true);
    if (iso.isValid()) return iso.format("DD-MMM-YYYY HH:mm");
    // try dd/MM/YYYY HH:mm:ss (your sample "12/11/2025 04:40:11")
    const dmy = moment(raw, "DD/MM/YYYY HH:mm:ss", true);
    if (dmy.isValid()) return dmy.format("DD-MMM-YYYY HH:mm");
    // fallback: let moment parse leniently
    const loose = moment(raw);
    return loose.isValid() ? loose.format("DD-MMM-YYYY HH:mm") : raw;
  };

  return (
    <>
      <div className="content">
        <div className="card">
          <div className="card-body">
            <div className="row">
              <div className="col-12">
                <DataGrid
                  ref={refDataGrid}
                  dataSource={assetPurchaseList}
                  keyExpr="requestQuotationId"
                  showColumnLines={false}
                  showRowLines={true}
                  showBorders={true}
                  allowColumnResizing={false}
                  allowColumnReordering={true}
                  columnAutoWidth={true}
                  onToolbarPreparing={(e) => {
                    e.toolbarOptions.items.unshift({
                      location: "before",
                      template: "toolbarRefreshTemplate",
                    });
                  }}
                >
                  {/* action column using requestQuotationId */}
                  <Column
                    dataField="requestQuotationId"
                    width={40}
                    caption=""
                    allowExporting={false}
                    allowFiltering={false}
                    allowSearch={false}
                    allowSorting={false}
                    cellRender={(cellData) => {
                      const row = cellData?.data;
                      const idPrefix = `icon-${row?.requestQuotationId}`;
                      return (
                        <div style={{ display: "flex" }}>
                          {props?.featurePermission?.canView && (
                            <span>
                              <IconWithTooltip
                                id={`${idPrefix}-view`}
                                className="iconify-size mr-1"
                                title="View"
                                tooltip="View"
                                icon="solar:document-text-outline"
                                style={{ cursor: "pointer" }}
                                onClick={() =>
                                  navigate("/asset-purchase-preview", {
                                    state: {
                                      requestQuotationId:
                                        row?.requestQuotationId,
                                      quotationIndentNo: row?.quotationIndentNo,
                                      quotationTypeId: row?.quotationTypeId,
                                      sourceUrl: "ReportAssetPurchase",
                                      isReport: true,
                                    },
                                  })
                                }
                              />
                            </span>
                          )}
                        </div>
                      );
                    }}
                  />

                  <Column
                    dataField="quotationIndentNo"
                    caption="Quotation No"
                    allowSearch={true}
                    allowFiltering={false}
                    allowSorting={true}
                  />

                  <Column
                    dataField="assetRequestNo"
                    caption="Asset Request No"
                    allowSearch={true}
                    allowFiltering={false}
                    allowSorting={true}
                  />

                  <Column
                    dataField="assetRequestSubNo"
                    caption="Asset Request Sub No"
                    allowSearch={true}
                    allowFiltering={false}
                    allowSorting={true}
                  />

                  <Column
                    dataField="assetStatusName"
                    caption="Status"
                    allowSearch={true}
                    allowFiltering={false}
                    allowSorting={true}
                    cellRender={(e) => {
                      let color = "#28A745";
                      if (
                        e.data?.statusId ===
                        AssetSubStatus.AssetQuotationInDraftMode
                      )
                        color = "#494545ff";
                      else if (
                        e.data?.statusId ===
                        AssetSubStatus.AssetQuotationApproved
                      )
                        color = "#4CAF50";
                      else if (
                        e.data?.statusId ===
                        AssetSubStatus.AssetQuotationSendforApproval
                      )
                        color = "#FF9800";

                      return (
                        <div>
                          <span style={{ color }}>
                            {e.data?.assetStatusName}
                          </span>
                          <div style={{ color: "#b1b1b1", fontSize: "11px" }}>
                            {e.data?.currentUserName ?? ""}
                          </div>
                        </div>
                      );
                    }}
                  />

                  <Column
                    dataField="requestedDate"
                    caption="Requested Date"
                    allowSearch={true}
                    allowFiltering={false}
                    allowSorting={true}
                    dataType="date"
                    format={DefaultDateFormat ?? "dd-MMM-yyyy"}
                    cellRender={(cell) => {
                      const raw = cell?.data?.requestedDate;
                      return <span>{renderDate(raw)}</span>;
                    }}
                  />

                  <Column
                    dataField="toProjectName"
                    caption="Project (To)"
                    allowSearch={true}
                    allowFiltering={false}
                    allowSorting={true}
                    cellRender={(data) => {
                      if (data?.data?.toProjectName) {
                        return (
                          <>
                            <span>{data?.data?.toProjectName ?? ""}</span>
                            <br />
                            <div style={{ color: "#b1b1b1", fontSize: "11px" }}>
                              {data?.data?.toProjectName ?? ""}
                            </div>
                          </>
                        );
                      } else {
                        return "";
                      }
                    }}
                  />

                  <Column
                    dataField="assetDescriptions"
                    caption="Asset Description"
                    allowSearch={true}
                    allowFiltering={false}
                    allowSorting={true}
                  />

                  <Column
                    dataField="fromName"
                    caption="From"
                    allowSearch={true}
                    allowFiltering={false}
                    allowSorting={true}
                    cellRender={(data) => {
                      if (data?.data?.fromName) {
                        return (
                          <>
                            <span>{data?.data?.fromName ?? ""}</span>
                            <br />
                            <div style={{ color: "#b1b1b1", fontSize: "11px" }}>
                              {data?.data?.fromDepartment ?? ""}
                            </div>
                          </>
                        );
                      } else return "";
                    }}
                  />

                  <Column
                    dataField="toName"
                    caption="To"
                    allowSearch={true}
                    allowFiltering={false}
                    allowSorting={true}
                    cellRender={(data) => {
                      if (data?.data?.toName) {
                        return (
                          <>
                            <span>{data?.data?.toName ?? ""}</span>
                            <br />
                            <div style={{ color: "#b1b1b1", fontSize: "11px" }}>
                              {data?.data?.toDepartment ?? ""}
                            </div>
                          </>
                        );
                      } else return "";
                    }}
                  />

                  <Column
                    dataField="purchaseNo"
                    caption="Purchase No"
                    allowSearch={true}
                    allowFiltering={false}
                    allowSorting={true}
                  />

                  <Column
                    dataField="companyName"
                    caption="Company"
                    allowSearch={true}
                    allowFiltering={false}
                    allowSorting={true}
                  />

                  <Template
                    name="toolbarRefreshTemplate"
                    render={() => {
                      return (
                        <>
                          <button
                            className="btn btn-outline-primary btn-sm"
                            onClick={async () => {
                              await getAssetPurchaseList();
                            }}
                            title="Refresh"
                          >
                            <FontAwesomeIcon icon={faRefresh} />
                            &nbsp;Refresh
                          </button>
                        </>
                      );
                    }}
                  />

                  <HeaderFilter visible={false} />
                  <SearchPanel
                    visible={true}
                    highlightCaseSensitive={true}
                    width={240}
                    placeholder="Search..."
                  />
                  <Paging defaultPageSize={15} />
                  <Pager displayMode="compact" showInfo={true} visible={true} />
                  <Export enabled={true} fileName="Asset Purchase Report" />
                </DataGrid>
              </div>
            </div>
          </div>
        </div>
      </div>

      {state.isCompleteLoading && (
        <Loader loadingMessage={state.loadingMessage} />
      )}
    </>
  );
}
